import { useCallback, useState } from 'react';
import { DATE_FILTERS, FILTER_TYPES } from '../constants';
import type { DateRangeErrors, Option } from '../interfaces/Dashboard';
import {
  emptyDateRangeErrors,
  resolveDateFilter,
  toSelectedDateRange,
  validateDateRange,
} from '../utils/dateRange';
import {
  isBoolean,
  isOptionIn,
  usePersistedState,
} from '../utils/persistedState';

/**
 * Filter state shared by the Landing page and the Health-Systemwise Summary
 * page: breakdown type, date preset, custom from/to dates and the
 * request-date toggle.
 *
 * Both pages ran identical ~120-line copies of this logic in the legacy app.
 *
 * `storageKey` namespaces the persisted breakdown-type/date-preset/toggle
 * selections per page, so choosing "Facility" here does not silently change
 * the other page's breakdown too. Custom from/to dates are intentionally not
 * persisted - they pair with per-field validation state that resets on
 * reload anyway, and "All" (the first option) already covers the common case
 * this feature is for.
 */

/**
 * Accepts a stored date preset only if it is a known option AND not "Custom".
 *
 * "Custom Date" is meaningless without the from/to pair, and those are
 * deliberately not persisted (see above), so a restored "Custom" would arrive
 * with both dates empty. `resolveDateFilter('Custom', '', '')` returns null
 * bounds - identical to "All" - and `validateDateRange('', '')` reports no
 * error, so the page would quietly query the full unbounded history while the
 * dropdown still read "Custom Date" next to two blank inputs. On a reporting
 * dashboard, showing someone unbounded data when they believe a date range is
 * applied is the wrong way to fail. Rejecting it here restores what the note
 * above always claimed: fall back to "All".
 */
const isPersistableDateFilter = (value: unknown): value is Option =>
  isOptionIn(DATE_FILTERS)(value) && value.value !== 'Custom';

export const useSummaryFilters = (storageKey: string) => {
  const [filterType, setFilterType] = usePersistedState<Option>(
    `${storageKey}:filterType`,
    FILTER_TYPES[0],
    isOptionIn(FILTER_TYPES),
  );
  const [dateFilter, setDateFilter] = usePersistedState<Option>(
    `${storageKey}:dateFilter`,
    DATE_FILTERS[0],
    isPersistableDateFilter,
  );
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [errors, setErrors] = useState<DateRangeErrors>(emptyDateRangeErrors());
  const [filterByRequestDate, setFilterByRequestDate] = usePersistedState(
    `${storageKey}:filterByRequestDate`,
    false,
    isBoolean,
  );

  /** Switching away from "Custom Date" discards any custom dates and errors. */
  const handleDateFilterChange = useCallback(
    (value: Option) => {
      setDateFilter(value);
      if (value.value !== 'Custom') {
        setFromDate('');
        setToDate('');
      }
      setErrors(emptyDateRangeErrors());
    },
    [setDateFilter],
  );

  const handleDateChange = useCallback(
    (field: 'fromdate' | 'todate', value: string) => {
      const nextFrom = field === 'fromdate' ? value : fromDate;
      const nextTo = field === 'todate' ? value : toDate;

      if (field === 'fromdate') setFromDate(value);
      else setToDate(value);

      setErrors(validateDateRange(nextFrom, nextTo));
    },
    [fromDate, toDate],
  );

  const reset = useCallback(() => {
    setFilterType(FILTER_TYPES[0]);
    setDateFilter(DATE_FILTERS[0]);
    setFromDate('');
    setToDate('');
    setErrors(emptyDateRangeErrors());
    setFilterByRequestDate(false);
  }, [setDateFilter, setFilterByRequestDate, setFilterType]);

  /**
   * Resolves the current selection into API params plus the heading labels.
   * Accepts explicit overrides so a "Clear then refetch" can use the defaults
   * without waiting for state updates to flush.
   */
  const resolve = useCallback(
    (overrides?: Partial<{ filterType: Option; dateFilter: Option }>) => {
      const activeFilterType = overrides?.filterType ?? filterType;
      const activeDateFilter = overrides?.dateFilter ?? dateFilter;
      const resolved = resolveDateFilter(
        activeDateFilter.value,
        fromDate,
        toDate,
      );

      return {
        resolved,
        filterType: activeFilterType,
        selectedDateRange: toSelectedDateRange(
          resolved,
          activeFilterType.value,
        ),
      };
    },
    [dateFilter, filterType, fromDate, toDate],
  );

  return {
    filterType,
    setFilterType,
    dateFilter,
    fromDate,
    toDate,
    errors,
    filterByRequestDate,
    setFilterByRequestDate,
    handleDateFilterChange,
    handleDateChange,
    reset,
    resolve,
  };
};
